"use client";

import type { CSSProperties, ReactNode } from "react";
import {
  ChevronDown,
  ChevronUp,
  PanelRightClose,
  PanelRightOpen,
} from "lucide-react";
import "./builderInspector.css";

type BuilderInspectorStyle = CSSProperties & {
  "--builder-inspector-toggle-right": string;
  "--builder-inspector-width": string;
};

type BuilderInspectorToggleProps =
  | {
      collapseLabel: string;
      expandLabel: string;
      onCollapsedChange: (collapsed: boolean) => void;
    }
  | {
      collapseLabel?: never;
      expandLabel?: never;
      onCollapsedChange?: undefined;
    };

export type BuilderInspectorShellProps = BuilderInspectorToggleProps & {
  appearance?: "studio" | "workspace";
  children: ReactNode;
  className?: string;
  collapsed: boolean;
  footer?: ReactNode;
  header?: ReactNode;
  tabs?: ReactNode;
  toolbar?: ReactNode;
  width?: number;
};

export function BuilderInspectorShell({
  appearance = "studio",
  children,
  className = "",
  collapseLabel,
  collapsed,
  expandLabel,
  footer,
  header,
  onCollapsedChange,
  tabs,
  toolbar,
  width = 400,
}: BuilderInspectorShellProps) {
  const safeWidth = Math.max(0, width);
  const inspectorStyle: BuilderInspectorStyle = {
    "--builder-inspector-toggle-right": `min(${Math.max(0, safeWidth - (appearance === "studio" ? 46 : 1))}px, calc(100vw - 45px))`,
    "--builder-inspector-width": `${safeWidth}px`,
  };

  return (
    <>
      <aside
        className={[
          "fixed bottom-[var(--app-frame-inset,0px)] left-[var(--app-frame-inset,0px)] right-[var(--app-frame-inset,0px)] top-auto z-[140] flex max-h-[calc(100dvh-5rem)] w-auto flex-col overflow-hidden border-border bg-surface text-text md:left-auto md:top-[calc(3.5rem+var(--app-frame-inset,0px))] md:h-auto md:max-h-none md:w-[min(var(--builder-inspector-width),calc(100vw-44px))]",
          appearance === "studio"
            ? "builder-inspector"
            : "h-[min(52dvh,420px)] rounded-b-[var(--app-frame-radius,0px)] border-t shadow-[0_20px_60px_rgba(0,0,0,0.24)] transition-transform duration-200 ease-out md:rounded-bl-none md:border-l md:border-t-0",
          collapsed ? "pointer-events-none" : "pointer-events-auto",
          appearance === "workspace" ? (
            collapsed
              ? "translate-y-[calc(100%+var(--app-frame-inset,0px))] md:translate-x-[calc(100%+var(--app-frame-inset,0px))] md:translate-y-0"
              : "translate-y-0 md:translate-x-0"
          ) : "",
          className,
        ].filter(Boolean).join(" ")}
        data-builder-inspector="true"
        data-builder-inspector-appearance={appearance}
        aria-hidden={collapsed}
        inert={collapsed ? true : undefined}
        style={inspectorStyle}
      >
        {toolbar ? (
          <div className="shrink-0" data-builder-inspector-toolbar="true">
            {toolbar}
          </div>
        ) : null}
        {tabs ? (
          <div className="shrink-0" data-builder-inspector-tabs="true">
            {tabs}
          </div>
        ) : null}
        {header ? (
          <div className="shrink-0" data-builder-inspector-header="true">
            {header}
          </div>
        ) : null}
        <div
          className="min-h-0 flex-1 overflow-auto overscroll-contain"
          data-builder-inspector-content="true"
        >
          {children}
        </div>
        {footer ? (
          <div className="shrink-0" data-builder-inspector-footer="true">
            {footer}
          </div>
        ) : null}
      </aside>

      {onCollapsedChange ? (
        <button
          type="button"
          className={[
            appearance === "studio"
              ? "builder-inspector-toggle pointer-events-auto fixed z-[180] grid h-10 w-10 place-items-center rounded-xl border border-border bg-surface text-text-secondary hover:bg-surface-hover hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/60"
              : "pointer-events-auto fixed right-[calc(0.5rem+var(--app-frame-inset,0px))] top-[calc(4.25rem+var(--app-frame-inset,0px))] z-[180] grid h-11 w-11 place-items-center rounded-full border border-cyan-400/60 bg-slate-950/95 text-cyan-300 shadow-lg transition-[right,background-color,border-color,color] duration-200 ease-out hover:border-cyan-300 hover:bg-slate-900 hover:text-cyan-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cyan-400/60 md:top-[calc(3.5rem+var(--app-frame-inset,0px))] md:h-10 md:w-9 md:rounded-l-md md:rounded-r-none md:border-r-0 md:shadow-none",
            appearance === "workspace" ? (
              collapsed
                ? "md:right-[var(--app-frame-inset,0px)]"
                : "md:right-[calc(var(--builder-inspector-toggle-right)+var(--app-frame-inset,0px))]"
            ) : "",
          ].join(" ")}
          style={inspectorStyle}
          data-builder-inspector-toggle="true"
          data-collapsed={collapsed}
          aria-expanded={!collapsed}
          aria-label={collapsed ? expandLabel : collapseLabel}
          title={collapsed ? expandLabel : collapseLabel}
          onClick={() => onCollapsedChange(!collapsed)}
        >
          <span className="md:hidden">
            {collapsed ? (
              <ChevronUp aria-hidden="true" className="h-5 w-5" />
            ) : (
              <ChevronDown aria-hidden="true" className="h-5 w-5" />
            )}
          </span>
          <span className="hidden md:block">
            {collapsed ? (
              <PanelRightOpen
                aria-hidden="true"
                className="h-[18px] w-[18px]"
                strokeWidth={1.8}
              />
            ) : (
              <PanelRightClose
                aria-hidden="true"
                className="h-[18px] w-[18px]"
                strokeWidth={1.8}
              />
            )}
          </span>
        </button>
      ) : null}
    </>
  );
}
